import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ConfirmContext, type ConfirmContextValue, type ConfirmOptions } from "./confirm-context";

type PendingConfirm = ConfirmOptions & { resolve: (value: boolean) => void };

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmContextValue>((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve });
    });
  }, []);

  const resolveWith = useCallback(
    (value: boolean) => {
      pending?.resolve(value);
      setPending(null);
    },
    [pending],
  );

  // Escape cancels, Enter confirms -- standard dialog keyboard behavior,
  // matching what a user expects from any confirm/alert dialog.
  useEffect(() => {
    if (!pending) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        resolveWith(false);
      } else if (event.key === "Enter") {
        event.preventDefault();
        resolveWith(true);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pending, resolveWith]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <div className="confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
          <div className="confirm-dialog">
            <h3 id="confirm-title">{pending.title}</h3>
            <p>{pending.message}</p>
            <div className="confirm-actions">
              <button type="button" className="secondary-btn" onClick={() => resolveWith(false)}>
                Cancel <span className="key-hint">Esc</span>
              </button>
              <button
                type="button"
                className={pending.danger ? "danger-btn" : "primary-btn"}
                onClick={() => resolveWith(true)}
                autoFocus
              >
                {pending.confirmLabel || "Confirm"} <span className="key-hint">Enter</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}
